import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Details, Field, Label, Paragraph, Select, Tag, ValidationMessage } from '@digdir/designsystemet-react';
import type { AssessedArea } from '../../../api/workEnvironmentTypes';
import {
  ASSESSMENT_FIELDS,
  AssessmentForm,
  draftPlacement,
  isMoved,
  MATRIX_SCALE,
  PlacementDraft,
  placementText,
} from '../assessmentView';
import { areaName, LEVEL_COLORS, placementLevel } from '../riskView';
import { TextField } from './FormParts';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  areas: AssessedArea[];
  form: AssessmentForm;
  levels: { moderateFrom: number; highFrom: number };
  errorFor: (fieldId: string) => string | undefined;
  onChange: (factorKey: string, draft: PlacementDraft | null) => void;
};

type RowProps = Omit<Props, 'areas'> & { area: AssessedArea };

const ScaleSelect = ({
  id,
  label,
  help,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  value: number;
  error?: string;
  onChange: (value: number) => void;
}) => (
  <Field data-size="sm">
    <Label htmlFor={id}>{label}</Label>
    <Field.Description>{help}</Field.Description>
    <Select
      id={id}
      value={value}
      aria-invalid={Boolean(error) || undefined}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {MATRIX_SCALE.map((step) => (
        <Select.Option key={step} value={step}>
          {step}
        </Select.Option>
      ))}
    </Select>
    {error && <ValidationMessage>{error}</ValidationMessage>}
  </Field>
);

const PlacementRow = ({ area, form, levels, errorFor, onChange }: RowProps): ReactElement => {
  const { t } = useTranslation();
  const moved = isMoved(area, form);
  const reasonId = ASSESSMENT_FIELDS.adjustmentReason(area.key);
  const [chosenOpen, setOpen] = useState(moved);
  // A field with an error stays open, so the link in the error summary finds it
  const open = chosenOpen || Boolean(errorFor(reasonId));
  const draft = draftPlacement(area, form);
  const level = placementLevel(draft, levels);
  const levelName = (value: string) => t(`workEnvironmentRisk.matrix.levels.${value}`);
  const name = areaName(area, t);
  const change = (next: Partial<PlacementDraft>) => onChange(area.key, { ...draft, ...next });

  return (
    <li className={classes.placementRow}>
      <div className={classes.areaSummary}>
        <span className={classes.areaTitle}>
          <span className="ds-label" data-size="sm">
            {name}
          </span>
          <span className="ds-paragraph" data-size="xs">
            {t('workEnvironmentRisk.assessment.calculated', { placement: placementText(area, t) })}
          </span>
        </span>
        <span className={classes.areaTags}>
          {moved && (
            <Tag data-size="sm" data-color="info">
              {t('workEnvironmentRisk.assessment.adjusted')}
            </Tag>
          )}
          {area.oneReport ? (
            <span className="ds-paragraph" data-size="xs">
              {t('workEnvironmentRisk.assessment.oneReportLocked')}
            </span>
          ) : (
            <Button
              type="button"
              variant="tertiary"
              data-size="sm"
              aria-expanded={open}
              aria-label={t(`workEnvironmentRisk.assessment.${open ? 'closeArea' : 'openArea'}`, { name })}
              onClick={() => setOpen(!open)}
            >
              {t(`workEnvironmentRisk.assessment.${open ? 'close' : 'open'}`)}
            </Button>
          )}
        </span>
      </div>
      {open && !area.oneReport && (
        <div className={classes.areaBody}>
          <div className={classes.placementFields}>
            <ScaleSelect
              id={ASSESSMENT_FIELDS.adjustment(area.key)}
              label={t('workEnvironmentRisk.assessment.consequence')}
              help={t('workEnvironmentRisk.assessment.consequenceHelp')}
              value={draft.consequence}
              error={errorFor(ASSESSMENT_FIELDS.adjustment(area.key))}
              onChange={(consequence) => change({ consequence })}
            />
            <ScaleSelect
              id={`assessment-likelihood-${area.key}`}
              label={t('workEnvironmentRisk.assessment.likelihood')}
              help={t('workEnvironmentRisk.assessment.likelihoodHelp')}
              value={draft.likelihood}
              onChange={(likelihood) => change({ likelihood })}
            />
          </div>
          <div role="status" className={classes.placementStatus}>
            <span className={classes.placementLevel}>
              <span className="ds-paragraph" data-size="sm">
                {moved
                  ? t('workEnvironmentRisk.assessment.newPlacement', {
                      consequence: draft.consequence,
                      likelihood: draft.likelihood,
                      from: levelName(area.level).toLowerCase(),
                    })
                  : t('workEnvironmentRisk.assessment.samePlacement')}
              </span>
              <Tag data-size="sm" data-color={LEVEL_COLORS[level]}>
                {levelName(level)}
              </Tag>
            </span>
            <span className="ds-paragraph" data-size="xs">
              {t(`workEnvironmentRisk.assessment.rules.${level}`)}
            </span>
          </div>
          {moved && (
            <>
              <TextField
                id={reasonId}
                label={t('workEnvironmentRisk.assessment.adjustReason')}
                help={t('workEnvironmentRisk.assessment.adjustReasonHelp')}
                value={draft.reason}
                error={errorFor(reasonId)}
                onChange={(reason) => change({ reason })}
              />
              <div>
                <Button type="button" variant="tertiary" data-size="sm" onClick={() => onChange(area.key, null)}>
                  {t('workEnvironmentRisk.assessment.reset')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
};

/** The team can move an area to another cell when they know something the numbers do not show */
export const PlacementAdjuster = ({ areas, ...props }: Props): ReactElement => {
  const { t } = useTranslation();
  const anyMoved = areas.some((area) => isMoved(area, props.form));
  return (
    <Details defaultOpen={anyMoved}>
      <Details.Summary>{t('workEnvironmentRisk.assessment.adjustTitle')}</Details.Summary>
      <Details.Content>
        <div className={classes.areaBody}>
          <Paragraph data-size="sm">{t('workEnvironmentRisk.assessment.adjustHelp')}</Paragraph>
          <ul className={classes.placementList}>
            {areas.map((area) => (
              <PlacementRow key={area.key} area={area} {...props} />
            ))}
          </ul>
        </div>
      </Details.Content>
    </Details>
  );
};
